import { createContext, useCallback, useContext, useState, type ReactNode } from "react";
import { ReservationModal, type BookingType } from "@/components/reservations/ReservationModal";
import { trackEvent } from "@/lib/analytics";

type OpenArgs = { bookingType?: BookingType };

type Ctx = {
  open: (args?: OpenArgs) => void;
};

const ReservationCtx = createContext<Ctx | null>(null);

export function ReservationProvider({ children }: { children: ReactNode }) {
  const [openState, setOpenState] = useState<{ open: boolean; bookingType: BookingType }>({
    open: false,
    bookingType: "food_and_drinks",
  });
  const open = useCallback((args?: OpenArgs) => {
    const bookingType: BookingType = args?.bookingType ?? "food_and_drinks";
    // Route to the specific CTA event GA4 expects.
    const eventName =
      bookingType === "pool_table"
        ? "book_pool_table_click"
        : bookingType === "group_booking"
          ? "group_booking_click"
          : "book_table_click";
    trackEvent(eventName, { booking_type: bookingType });
    setOpenState({ open: true, bookingType });
  }, []);
  const close = useCallback(() => setOpenState((s) => ({ ...s, open: false })), []);
  return (
    <ReservationCtx.Provider value={{ open }}>
      {children}
      <ReservationModal
        open={openState.open}
        onOpenChange={(v) => (v ? null : close())}
        initialBookingType={openState.bookingType}
      />
    </ReservationCtx.Provider>
  );
}

export function useReservationModal() {
  const ctx = useContext(ReservationCtx);
  if (!ctx) throw new Error("useReservationModal must be used inside ReservationProvider");
  return ctx;
}

/** Imperative helper: works anywhere inside the provider tree. */
export function openReservationModalFactory(open: (args?: OpenArgs) => void) {
  return (args?: OpenArgs) => open(args);
}
